/**
 * SEO Data Configuration for Makrana Marble Website
 * Centralized SEO management with page-specific optimizations
 */

const baseUrl = "https://makrana-marble.com";
const siteName = "Makrana Marble";

// Default Open Graph image
const defaultOGImage = "/images/site/makrana-marble-og.jpg";

// Company information
const companyInfo = {
  name: "Makrana Marble",
  description: "Premium quality Makrana marble handicrafts, temple carvings, and architectural elements from Rajasthan's finest artisans",
  phone: "+91-7014351132",
  email: "info@makrana-marble.com",
  address: {
    locality: "Makrana",
    region: "Rajasthan", 
    country: "India",
    postalCode: "341505"
  },
  socialMedia: {
    whatsapp: "https://wa.me/917014351132",
    facebook: "https://www.facebook.com/makranamarble",
    instagram: "https://www.instagram.com/makranamarble"
  }
};

/**
 * Home Page SEO Configuration - Optimized for High Traffic
 */
export const homeSEO = {
  title: "Makrana Marble - Best Quality Marble Handicrafts, Temple Carving & Slabs Online",
  description: "Buy authentic Makrana marble handicrafts, temple carvings, mosque architecture, and premium quality marble slabs online. India's #1 marble artisans with 20+ years experience. Free shipping across India.",
  keywords: "makrana marble, buy marble online, marble handicrafts, temple carving, mosque architecture, marble slabs, rajasthan marble, marble inlay work, authentic marble, premium marble, marble artisans, best marble price, marble online shopping, indian marble, white marble, marble supplier, marble manufacturer, marble for home, marble tiles, marble flooring",
  image: defaultOGImage,
  url: baseUrl,
  type: "website",
  structuredData: {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": companyInfo.name,
    "description": companyInfo.description,
    "url": baseUrl,
    "telephone": companyInfo.phone,
    "email": companyInfo.email,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": companyInfo.address.locality,
      "addressRegion": companyInfo.address.region,
      "addressCountry": companyInfo.address.country,
      "postalCode": companyInfo.address.postalCode
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 27.0467,
      "longitude": 74.4414
    },
    "openingHours": [
      "Mo-Sa 09:00-18:00"
    ],
    "priceRange": "$$",
    "servedCuisine": "Traditional Marble Craftsmanship",
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 27.0467,
        "longitude": 74.4414
      },
      "geoRadius": "500000"
    }
  }
};

/**
 * About Page SEO Configuration  
 */
export const aboutSEO = {
  title: "About Us - 20+ Years of Makrana Marble Craftsmanship Excellence",
  description: "Learn about our heritage in Makrana marble craftsmanship. Family-owned business with 20+ years of experience creating premium marble handicrafts, temples, and architectural elements.",
  keywords: "makrana marble history, marble artisans, marble craftsmanship, rajasthan marble heritage, authentic marble work, marble craftsmen",
  image: "/images/site/about-makrana-marble.jpg",
  canonicalUrl: "/about",
  breadcrumbs: [
    { name: "Home", url: "/" },
    { name: "About Us", url: "/about" }
  ],
  article: {
    headline: "Our Story - Preserving Makrana Marble Traditions",
    datePublished: "2024-01-01T00:00:00Z",
    dateModified: new Date().toISOString()
  }
};

/**
 * Contact Page SEO Configuration
 */
export const contactSEO = {
  title: "Contact Us - Get Quote for Premium Makrana Marble Products",
  description: "Contact Makrana Marble for custom orders, quotes, and inquiries. WhatsApp +91-7014351132 for immediate response. Located in Makrana, Rajasthan.",
  keywords: "contact makrana marble, marble quote, custom marble order, makrana marble phone, marble inquiry",
  image: "/images/site/contact-makrana-marble.jpg",
  canonicalUrl: "/contact",
  breadcrumbs: [
    { name: "Home", url: "/" },
    { name: "Contact", url: "/contact" }
  ]
};

/**
 * Products Page SEO Configuration
 */
export const productsSEO = {
  title: "Premium Marble Products - Handicrafts, Temples, Slabs & Custom Work",
  description: "Explore our extensive collection of Makrana marble products including handicrafts, temple carvings, mosque architecture, decorative slabs, and custom marble work.",
  keywords: "marble products, marble handicrafts, temple marble, mosque marble, marble slabs, custom marble work, decorative marble",
  image: "/images/site/products-makrana-marble.jpg",
  canonicalUrl: "/products",
  breadcrumbs: [
    { name: "Home", url: "/" },
    { name: "Products", url: "/products" }
  ]
};

/**
 * Categories SEO Configuration
 */
export const categoriesSEO = {
  title: "Marble Categories - Browse Our Complete Product Range",
  description: "Browse our complete range of marble product categories including temples, handicrafts, mosques, inlay work, fountains, and raw marble materials.",
  keywords: "marble categories, marble types, temple marble, handicraft marble, mosque marble, inlay marble, fountain marble",
  image: "/images/site/categories-makrana-marble.jpg",
  canonicalUrl: "/categories",
  breadcrumbs: [
    { name: "Home", url: "/" },
    { name: "Categories", url: "/categories" }
  ]
};

/**
 * Category-specific SEO configurations
 */
export const categoryPageSEO = {
  temples: {
    title: "Temple Marble Carvings - Authentic Makrana Marble Temple Architecture",
    description: "Exquisite temple marble carvings and architecture in authentic Makrana marble. Custom temple designs, mandirs, and spiritual marble artwork by master craftsmen.",
    keywords: "temple marble, marble temple, mandir marble, temple carving, spiritual marble, marble temple architecture",
    image: "/images/temples/temple-main.jpg"
  },
  handicrafts: {
    title: "Marble Handicrafts - Handcrafted Decorative Marble Items", 
    description: "Beautiful handcrafted marble handicrafts including decorative items, figurines, vases, and artistic pieces. Premium Makrana marble handicrafts for home and office.",
    keywords: "marble handicrafts, marble decorative items, marble figurines, marble vases, marble art pieces",
    image: "/images/handicrafts/handicraft-main.jpg"
  },
  mosques: {
    title: "Mosque Marble Architecture - Islamic Marble Art & Design",
    description: "Sophisticated mosque marble architecture and Islamic art in authentic Makrana marble. Custom mosque designs, mihrab, and Islamic calligraphy work.",
    keywords: "mosque marble, islamic marble art, mosque architecture, mihrab marble, islamic calligraphy marble",
    image: "/images/mosques/mosque-main.jpg"
  },
  inlay: {
    title: "Marble Inlay Work - Pietra Dura Art in Makrana Marble",
    description: "Intricate marble inlay work (Pietra Dura) featuring precious stone designs. Traditional Mughal-style inlay art in premium Makrana marble.",
    keywords: "marble inlay, pietra dura, marble inlay work, stone inlay, mughal marble art",
    image: "/images/inlay/inlay-main.jpg"
  },
  raw: {
    title: "Raw Marble Materials - Premium Makrana Marble Blocks & Slabs",
    description: "High-quality raw Makrana marble materials including blocks, slabs, and unfinished marble for construction and crafting projects.",
    keywords: "raw marble, marble blocks, marble slabs, makrana marble material, construction marble",
    image: "/images/raw/raw-main.jpg"
  }
};

/**
 * Generate dynamic SEO for category pages
 */
export const generateCategorySEO = (category, products = []) => {
  const baseSEO = categoryPageSEO[category];
  if (!baseSEO) return productsSEO;

  return {
    ...baseSEO,
    canonicalUrl: `/category/${category}`,
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Categories", url: "/categories" },
      { name: baseSEO.title.split(' - ')[0], url: `/category/${category}` }
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": baseSEO.title,
      "description": baseSEO.description,
      "url": `${baseUrl}/category/${category}`,
      "mainEntity": {
        "@type": "ItemList",
        "numberOfItems": products.length,
        "itemListElement": products.slice(0, 10).map((product, index) => ({
          "@type": "Product",
          "position": index + 1,
          "name": product.name,
          "description": product.description,
          "image": `${baseUrl}${product.image}`,
          "category": category,
          "material": "Marble"
        }))
      }
    }
  };
};

/**
 * Generate SEO for individual product pages
 */
export const generateProductSEO = (product, category) => {
  return {
    title: `${product.name} - Premium Makrana Marble ${category}`,
    description: `${product.description || `Beautiful ${product.name} crafted from premium Makrana marble.`} Authentic ${category} work by skilled artisans.`,
    keywords: `${product.name}, ${category} marble, makrana marble ${category}, ${product.name.toLowerCase()}`,
    image: product.image || `/images/${category}/${product.name.toLowerCase().replace(/\s+/g, '-')}.jpg`,
    canonicalUrl: `/product/${product.id}`,
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Categories", url: "/categories" },
      { name: category.charAt(0).toUpperCase() + category.slice(1), url: `/category/${category}` },
      { name: product.name, url: `/product/${product.id}` }
    ],
    product: {
      name: product.name,
      description: product.description,
      category: category,
      images: product.images || [product.image],
      price: product.price
    }
  };
};

// Export configuration
export const seoConfig = {
  baseUrl,
  siteName,
  companyInfo,
  defaultOGImage
};

const seoData = {
  homeSEO,
  aboutSEO,
  contactSEO,
  productsSEO,
  categoriesSEO,
  categoryPageSEO,
  generateCategorySEO,
  generateProductSEO,
  seoConfig
};

export default seoData;